/**
 * Shared presentational pieces for the calculator pages: the method
 * explanation block, FAQ list, open Sources section, the Advanced (regional
 * adjustment) collapsible, and small labelled form-field helpers. Kept here so
 * the three calculator pages stay focused on their own inputs and API call.
 */
import { useId, useState, type ReactNode } from 'react';

import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { DimensionDivider } from './ToolPageShell';

// --- Content sections -----------------------------------------------------

export function MethodSection({
  title = 'How this estimate is calculated',
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <section className="mt-14">
      <DimensionDivider label="Method" />
      <h2 className="mb-3 mt-5 font-ui text-2xl font-bold tracking-tight">
        {title}
      </h2>
      <div className="max-w-3xl space-y-3 text-[15px] leading-relaxed text-muted-foreground">
        {children}
      </div>
    </section>
  );
}

export interface FaqItem {
  q: string;
  a: string;
}

export function FaqSection({ items }: { items: FaqItem[] }) {
  return (
    <section className="mt-14">
      <DimensionDivider label="FAQ" />
      <h2 className="mb-4 mt-5 font-ui text-2xl font-bold tracking-tight">
        Common questions
      </h2>
      <dl className="max-w-3xl divide-y divide-border">
        {items.map((item, i) => (
          <div key={i} className="py-4 first:pt-0 last:pb-0">
            <dt className="font-semibold text-foreground">{item.q}</dt>
            <dd className="mt-1.5 text-[15px] leading-relaxed text-muted-foreground">
              {item.a}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export interface SourceRef {
  name: string;
  detail: string;
  url?: string;
}

export function SourcesSection({ sources }: { sources: SourceRef[] }) {
  return (
    <section className="mt-14">
      <DimensionDivider label="Rate basis" />
      <h2 className="mb-4 mt-5 font-ui text-2xl font-bold tracking-tight">
        Sources
      </h2>
      <ul className="max-w-3xl space-y-2.5 text-sm text-muted-foreground">
        {sources.map((s, i) => (
          <li key={i} className="flex gap-3">
            <span
              aria-hidden
              className="mt-1.5 h-2.5 w-px shrink-0 bg-amber-500"
            />
            <span>
              {s.url ? (
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-foreground underline underline-offset-2 hover:opacity-80"
                >
                  {s.name}
                </a>
              ) : (
                <span className="font-medium text-foreground">{s.name}</span>
              )}
              {' — '}
              {s.detail}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// --- Advanced (regional adjustment) collapsible ---------------------------

export function AdvancedRegional({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <div className="mt-4 border-t border-border pt-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-sm font-medium text-primary hover:underline"
        aria-expanded={open}
      >
        {open ? 'Hide advanced options' : 'Advanced options'}
      </button>
      {open && (
        <div className="mt-3 space-y-1.5">
          <Label htmlFor={id}>Regional adjustment (%)</Label>
          <Input
            id={id}
            type="number"
            step="1"
            value={Number.isFinite(value) ? value : 0}
            onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          />
          <p className="text-xs text-muted-foreground">
            Rates basis: published public rate schedules (Caltrans and USACE).
            Adjust for your market. Leave at 0 to use the published rates as is.
          </p>
        </div>
      )}
    </div>
  );
}

// --- Form field helpers ---------------------------------------------------

export function NumberField({
  label,
  value,
  onChange,
  min,
  step = 'any',
  suffix,
  hint,
}: {
  label: string;
  value: number | '';
  onChange: (v: number) => void;
  min?: number;
  step?: string;
  suffix?: string;
  hint?: string;
}) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}
        {suffix && (
          <span className="font-normal text-muted-foreground">
            {' '}
            ({suffix})
          </span>
        )}
      </Label>
      <Input
        id={id}
        type="number"
        min={min}
        step={step}
        value={value}
        onChange={(e) =>
          onChange(e.target.value === '' ? NaN : parseFloat(e.target.value))
        }
      />
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function SelectField<T extends string | number>({
  label,
  value,
  onChange,
  options,
  hint,
}: {
  label: string;
  value: T;
  // Reason: the native <select> always yields a string; numeric callers
  // re-coerce with Number() at the call site (water class/category).
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  hint?: string;
}) {
  const id = useId();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function CheckboxField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className="flex cursor-pointer items-center gap-2 text-sm text-foreground"
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-input"
      />
      {label}
    </label>
  );
}
